import { useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { useVersionManifest } from "@/api/version/queries";
import { useAppUpdateStore } from "../stores/use-app-update-store";
import { shouldAutoReload, trackAutoReload } from "../utils/auto-reload-guard";
import { getCurrentBuildId } from "../utils/build-id";
import { isSafeReloadPath } from "../utils/is-safe-reload-path";
import { reloadApp } from "../utils/reload-app";

/**
 * useAutoRefresh hook
 * Transparently keeps the open tab on the latest published build. It polls the
 * deployed version manifest, records when a newer build exists, then hard-reloads
 * on the next navigation to a safe route — never interrupting in-progress work.
 * A dynamic-import (preload) failure forces an immediate reload as a safety net.
 * Each trigger reloads at most once per build id, so a CDN still propagating a
 * deploy or a persistent chunk failure cannot cause a reload loop.
 */
export const useAutoRefresh = () => {
  /* Queries */
  const { data } = useVersionManifest();

  /* Hooks */
  const [location] = useLocation();
  const previousLocation = useRef(location);
  const availableBuildId = useAppUpdateStore((state) => state.availableBuildId);
  const setAvailableBuildId = useAppUpdateStore((state) => state.setAvailableBuildId);

  // Record the newer build whenever the deployed build id differs from this tab's build.
  useEffect(() => {
    if (data && data.buildId !== getCurrentBuildId()) {
      setAvailableBuildId(data.buildId);
    }
  }, [data, setAvailableBuildId]);

  // Reload when the user navigates to a safe, stateless route while a newer
  // build is pending. Reloading only on an actual navigation — never while
  // parked on a safe route — so open modals or half-filled forms are not
  // discarded.
  useEffect(() => {
    const hasNavigated = location !== previousLocation.current;
    previousLocation.current = location;

    if (!hasNavigated || !availableBuildId || !isSafeReloadPath(location)) return;
    if (!shouldAutoReload("new-version", availableBuildId)) return;

    trackAutoReload("new-version", availableBuildId);
    reloadApp();
  }, [location, availableBuildId]);

  // Safety net: a tab on an old build that requests a now-missing chunk throws a
  // preload error. Force a reload to fetch the freshly published assets.
  // preventDefault stops Vite rethrowing, so the Sentry error boundary never
  // flashes in the moment before the reload lands. If a reload for this build
  // already failed to fix it, the error is left to surface rather than looping.
  useEffect(() => {
    const handlePreloadError = (event: Event) => {
      const currentBuildId = getCurrentBuildId();

      if (!shouldAutoReload("preload-error", currentBuildId)) return;

      event.preventDefault();
      trackAutoReload("preload-error", currentBuildId);
      reloadApp();
    };

    window.addEventListener("vite:preloadError", handlePreloadError);

    return () => window.removeEventListener("vite:preloadError", handlePreloadError);
  }, []);
};
